// tslint:disable:no-console
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { TELEMETRY_HTTP_CLIENT, TelemetryHttpClient } from '@rccl/telemetry-angular';
import { Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, delay, retryWhen, take, tap, timeout } from 'rxjs/operators';
import { Brand } from '~app/shared/enums/brand.enum';
import { getTimestamp } from '~app/shared/functions/utils/utils.function';
import { IGetCountryListResponse } from '~app/shared/services/mw/interfaces/GetCountryList.interface';
import { IGetCrewAssignmentsResponse } from '~app/shared/services/mw/interfaces/GetCrewAssignments.interface';
import { IGetCrewEffectsResponse } from '~app/shared/services/mw/interfaces/GetCrewEffects.interface';
import { IGetCrewEffectsMasterResponse } from '~app/shared/services/mw/interfaces/GetCrewEffectsMaster.interface';
import {
  IBeneficiaryContact,
  IEmergencyContact,
  IGetCrewMemberProfileResponse,
} from '~app/shared/services/mw/interfaces/GetCrewMemberProfile.interface';
import { IGetEmployeeAgreementInfoResponse } from '~app/shared/services/mw/interfaces/GetEmployeeAgreementInfo.interface';
import { IGetShipSignOnsResponse } from '~app/shared/services/mw/interfaces/GetEnhancedShipSignons.interface';
import { IGetPhoneTypeMasterResponse } from '~app/shared/services/mw/interfaces/GetPhoneTypeMaster.interface';
import { IGetPortCodeMasterResponse } from '~app/shared/services/mw/interfaces/GetPortCodeMaster.interface';
import { IGetShipListResponse } from '~app/shared/services/mw/interfaces/GetShipList.interface';
import { IGetSignOnPoliciesAcknowledgementResponse } from '~app/shared/services/mw/interfaces/GetSignOnPoliciesAcknowledgement.interface';
import { IGetWorkflowStateResponse } from '~app/shared/services/mw/interfaces/GetWorkflowState.interface';
import { IMWStandardResponse } from '~app/shared/services/mw/interfaces/MWStandardResponse.interface';
import { environment } from '~env';
import {
  IDocusignConsoleViewParams,
  IDocusignRecipientViewParams,
  IGetDocusignResponse,
} from './interfaces/Docusign.interface';
import { IGetContentDiseases } from './interfaces/GetContentDiseases.interface';
import { IGetContentFAQS } from './interfaces/GetContentFAQS.interface';
import { ICharacteristics, IGetCrewCrewPhysiqueResponse } from './interfaces/GetCrewPhysique.interface';
import { IGetHealthCheckSurveyResponse } from './interfaces/GetHealthCheckSurvey.interface';
import { IGetHealthCheckSurveyContent } from './interfaces/GetHealthCheckSurveyContent';
import { SplunkService, SpinnerService, AuthService } from '~app/shared/services';
import { EndPointsErrorLookup } from '~app/shared/types/endpoints-error-codes.type';
import { IGetCovidVaxInfoResponse } from '~app/check-in/interfaces/covid-vax-info-response.interface';
import { IVaccineCard } from '~app/check-in/interfaces/vaccine-card.interface';
import { IVaccineManufacturerListResponse } from '~app/shared/interfaces/vaccine-manufacturer-list-response.interface';
import { IGetShipSignoffsResponse } from './interfaces/GetEnhancedShipSignoffs.interface';
import { Profile } from '~app/shared/models/profile.model';
import { IHttpClientOptions } from './interfaces/HttpClientOptions.interface';

interface ICacheItem {
  payload: IMWStandardResponse;
  date: number;
  invalid: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class MwService {
  private readonly host = environment.mw.host;
  private readonly locale = environment.mw.locale;
  private readonly brand = environment.mw.brand;
  private readonly channel = environment.mw.channel;
  private readonly shortCacheTime = environment.mw.shortCacheTime;
  private readonly longCacheTime = environment.mw.longCacheTime;
  private readonly appKey = environment.mw.appKey;

  private cache: {
    [key: string]: ICacheItem;
  } = {};

  constructor(
    @Inject(TELEMETRY_HTTP_CLIENT) private http: TelemetryHttpClient,
    private readonly auth: AuthService,
    private readonly spinner: SpinnerService,
    private readonly splunkService: SplunkService,
  ) {
    (window as any)._mwCache = this.cache;
  }

  private get baseUrl(): string {
    return `${this.host}/${this.locale}/${this.brand}/${this.channel}`;
  }

  private get defaultHeaders(): HttpHeaders {
    return new HttpHeaders()
      .set('AppKey', this.appKey)
      .set('Crew-Member-Id', this.auth.crewMemberId)
      .set('Session-Id', this.auth.sessionId)
      .set('Content-Type', 'application/json')
      .set('Cache-Control', 'no-cache')
      .set('Pragma', 'no-cache');
  }

  private get defaultOptions(): {
    headers: HttpHeaders;
    [key: string]: any;
  } {
    return {
      headers: this.defaultHeaders,
    };
  }

  public invalidateCache(): void {
    this.cache = {};
  }

  public getCountryList(): Observable<IGetCountryListResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v2/generics/countries',
      cacheTime: this.longCacheTime,
      useAuthentication: false,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting country list';
        const lookup: EndPointsErrorLookup = 'PC-001-GET-COUNTRY-LIST-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getVaccineManufacturerList(): Observable<IVaccineManufacturerListResponse> {
    return of({
      errors: [],
      payload: {
        brands: [
          {
            label: 'Moderna (Spikevax)',
            value: '207',
          },
          {
            label: 'Pfizer (Comirnaty)',
            value: '208',
          },
          {
            label: 'Astrazeneca (Covishield)',
            value: '210',
          },
          {
            label: 'Johnson & Johnson (Janssen)',
            value: '212',
          },
          {
            label: 'Sinopharm BBIBP-CorV Beijing',
            value: '510',
          },
          {
            label: 'Sinovac (Coronavac)',
            value: '511',
          },
          {
            label: 'Covaxin',
            value: '903',
          },
          {
            label: 'Sputnik V',
            value: '511',
          },
          {
            label: 'Cansino',
            value: '506',
          },
          {
            label: 'Zflongkema',
            value: '906',
          },
          {
            label: 'Sputnik Lite',
            value: '504',
          },
          {
            label: 'Corbevax',
            value: '517',
          },
          {
            label: 'Sinopharm WIBP Wuhan',
            value: '996',
          },
          {
            label: 'Sinopharm (unspecified code/origin)',
            value: '995',
          },
        ],
      },
      status: 200,
    });
  }

  public getPortCodeMaster(): Observable<IGetPortCodeMasterResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v1/employee/master/ports',
      cacheTime: Infinity,
      useAuthentication: false,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting ports list';
        const lookup: EndPointsErrorLookup = 'PC-002-GET-PORT-INFO-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getShipList(): Observable<IGetShipListResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v1/employee/master/ships',
      cacheTime: this.longCacheTime,
      useAuthentication: false,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting ship list';
        const lookup: EndPointsErrorLookup = 'PC-003-GET-SHIP-CODE-MASTER-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getCrewAssignments(customCrewMemberId?: string): Observable<IGetCrewAssignmentsResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v1/employee/assignments',
      cacheTime: this.shortCacheTime,
      crewMemberId: customCrewMemberId,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting crew assignments list';
        const lookup: EndPointsErrorLookup = 'PC-004-GET-CREW-ASSIGNMENTS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getCrewEffectsMaster(): Observable<IGetCrewEffectsMasterResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v2/employee/crewEffects/master',
      cacheTime: this.longCacheTime,
      useAuthentication: false,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting crew effects master';
        const lookup: EndPointsErrorLookup = 'PC-006-GET-CREW-EFFECTS-V2-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getCrewEffects(customCrewMemberId?: string): Observable<IGetCrewEffectsResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v2/employee/crewEffects/customInfo',
      crewMemberId: customCrewMemberId,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting crew effects';
        const lookup: EndPointsErrorLookup = 'PC-005-GET-CREW-EFFECTS-MASTER-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getCrewMemberProfile(customCrewMemberId?: string): Observable<IGetCrewMemberProfileResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v1/employee/profiles',
      crewMemberId: customCrewMemberId,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting crew profile';
        const lookup: EndPointsErrorLookup = 'PC-007-GET-CREW-MEMBER-PROFILE-V1-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  // tslint:disable-next-line: max-line-length
  public getEnhancedShipSignons(
    shipCode: string,
    fromSignonDate: string,
    toSignonDate: string = fromSignonDate,
  ): Observable<IGetShipSignOnsResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/assignments/enhancedShipSignons/ships/${shipCode}/signonDates/${fromSignonDate}/${toSignonDate}`,
      cacheTime: 1000,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error enhanced ship signons';
        const lookup: EndPointsErrorLookup = 'PC-008-GET-ENHANCED-SHIP-SIGNONS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getShipSignoffs(shipCode: string, fromSignoffDate: string): Observable<IGetShipSignoffsResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/assignments/shipSignoffs/ship/${shipCode}/signoffDate/${fromSignoffDate}`,
      cacheTime: 1000,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting ship signoffs';
        const lookup: EndPointsErrorLookup = 'PC-039-GET-SHIP-SIGNOFFS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getSignOnPoliciesAcknowledgement(
    shipCode: string,
    signonDate: string,
    customCrewMemberId?: string,
  ): Observable<IGetSignOnPoliciesAcknowledgementResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/policies/acknowledgement/signonDates/${signonDate}/ships/${shipCode}`,
      cacheTime: this.shortCacheTime,
      crewMemberId: customCrewMemberId,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting signon policies acknowledgement';
        const lookup: EndPointsErrorLookup = 'PC-009-GET-SIGN-ON-POLICIES-ACKNOWLEDGEMENTS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getSignOffPoliciesAcknowledgement(
    shipCode: string,
    signoffDate: string,
    customCrewMemberId?: string,
  ): Observable<IGetSignOnPoliciesAcknowledgementResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/policies/acknowledgement/signoffDates/${signoffDate}/ships/${shipCode}`,
      cacheTime: this.shortCacheTime,
      crewMemberId: customCrewMemberId,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting signoff policies acknowledgement';
        const lookup: EndPointsErrorLookup = 'PC-040-GET-SIGN-OFF-POLICIES-ACKNOWLEDGEMENT-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getAssignments(shipCode: string, crewMemberId: string): Observable<IGetCrewAssignmentsResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/assignments/shipSignons/ships/${shipCode}/crewMemberId/${crewMemberId}`,
      cacheTime: this.shortCacheTime,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting assignments';
        const lookup: EndPointsErrorLookup = 'PC-010-GET-ASSIGNMENTS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateBeneficiaryandEmergencyContacts(
    contacts: Array<IBeneficiaryContact | IEmergencyContact>,
  ): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/profiles',
      body: {
        crewMemberId: this.auth.crewMemberId,
        timestamp: getTimestamp(new Date()),
        contacts,
      },
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating beneficiary and emergency contacts';
        const lookup: EndPointsErrorLookup = 'PC-011-POST-UPDATE-BENEFICIARY-AND-EMERGENCY-CONTACTS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateSignOnPoliciesAcknowledgement(
    shipCode: string,
    signonDate: string,
    policies: Array<{
      policyIdentifier: string;
      contentVersion: string;
    }>,
  ): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/policies/acknowledgement',
      body: {
        crewMemberId: this.auth.crewMemberId,
        timestamp: getTimestamp(new Date()),
        shipCode,
        signonDate,
        policies,
      },
      cacheTime: 1,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating signon policies acknowledgement';
        const lookup: EndPointsErrorLookup = 'PC-012-POST-UPDATE-SIGN-ON-POLICIES-ACKNOWLEDGEMENTS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateSignOffPoliciesAcknowledgement(
    shipCode: string,
    signoffDate: string,
    policies: Array<{
      policyIdentifier: string;
      contentVersion: string;
    }>,
  ): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/policies/acknowledgement/debark',
      body: {
        crewMemberId: this.auth.crewMemberId,
        timestamp: getTimestamp(new Date()),
        shipCode,
        signoffDate,
        policies,
      },
      cacheTime: 1,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating signoff policies acknowledgement';
        const lookup: EndPointsErrorLookup = 'PC-041-PUT-SIGN-OFF-POLICIES-ACKNOWLEDGEMENT-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateCrewEffects(items: { [key: string]: number }): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v2/employee/crewEffects',
      body: {
        crewMemberId: this.auth.crewMemberId,
        items,
        lastUpdated: getTimestamp(new Date()),
      },
      cacheTime: 1,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating crew effects';
        const lookup: EndPointsErrorLookup = 'PC-037-POST-UPDATE-CREW-EFFECTS-V2-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getEmployeeAgreementInfo(type: string, brand?: Brand): Observable<IGetEmployeeAgreementInfoResponse> {
    let b: string;
    switch (brand) {
      case 'CCI':
        b = 'celebrity';
        break;
      case 'ACC':
        b = 'azamara';
        break;
      case 'RCI':
        b = 'royal';
        break;
      default:
        console.warn('brand not found, defaulting to royal');
        b = 'royal';
        break;
    }
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v3/agreement/employee?type=${type}`,
      cacheTime: Infinity,
      useAuthentication: true,
      baseUrl: `${this.host}/${this.locale}/${b}/web`,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting employee agreement info';
        const lookup: EndPointsErrorLookup = 'PC-013-GET-AGREEMENT-INFO-V3-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getPhoneTypeMaster(): Observable<IGetPhoneTypeMasterResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v1/employee/master/phoneTypes',
      cacheTime: this.longCacheTime,
      useAuthentication: false,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting phone type master';
        const lookup: EndPointsErrorLookup = 'PC-014-GET-TELEPHONE-TYPE-MASTER-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getSOEAFromEFS(doc: string): Observable<any> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/serviceAgreements/${doc}`,
      cacheTime: this.longCacheTime,
      responseType: 'blob',
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting SOEA from EFS';
        const lookup: EndPointsErrorLookup = 'PC-015-GET-SOEA-FROM-EFS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getReportFromEFS(report: string, shipCode?: string): Observable<any> {
    const headers = shipCode ? new HttpHeaders().set('Ship-Codes', shipCode) : undefined;
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/reports/${report}/csv`,
      cacheTime: 0,
      responseType: 'text',
      additionalHeaders: headers,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting report from EFS';
        const lookup: EndPointsErrorLookup = 'PC-016-GET-REPORT-CSV-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getHealthCheckSurvey(
    shipCode: string,
    signonDate: string,
    customCrewMemberId?: string,
  ): Observable<IGetHealthCheckSurveyResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/healthCheck/survey/ships/${shipCode}/signonDates/${signonDate}`,
      cacheTime: this.shortCacheTime,
      crewMemberId: customCrewMemberId,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting health check survey';
        const lookup: EndPointsErrorLookup = 'PC-017-GET-EMPLOYEE-HEALTH-CHECK-SURVEY-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getHealthCheckVaccinesInfo(): Observable<IGetCovidVaxInfoResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v1/employee/healthCheck/vaccine/doses',
      cacheTime: this.shortCacheTime,
      crewMemberId: this.auth.crewMemberId,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting health check vaccines info';
        const lookup: EndPointsErrorLookup = 'PC-035-GET-HEALTH-CHECK-VACCINES-INFO-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateHealthCheckSurvey(
    shipCode: string,
    signonDate: string,
    hasSymptoms: boolean,
  ): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/healthCheck/survey',
      body: {
        crewMemberId: this.auth.crewMemberId,
        lastUpdated: getTimestamp(new Date(), true),
        shipCode,
        signonDate,
        hasSymptoms,
      },
      cacheTime: 1,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating health check survey';
        const lookup: EndPointsErrorLookup = 'PC-018-POST-UPDATE-HEALTH-CHECK-SURVEY-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getWorkflowState(
    shipCode: string,
    signOnDate: string,
    customCrewMemberId?: string,
  ): Observable<IGetWorkflowStateResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v2/employee/checkin/workflow/ships/${shipCode}/signonDates/${signOnDate}`,
      cacheTime: this.shortCacheTime,
      crewMemberId: customCrewMemberId,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting workflow state';
        const lookup: EndPointsErrorLookup = 'PC-019-GET-CHECK-IN-WORKFLOW-STATE-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getCheckOutWorkflowState(
    shipCode: string,
    signOffDate: string,
    customCrewMemberId?: string,
  ): Observable<IGetWorkflowStateResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v2/employee/checkout/workflow/ships/${shipCode}/signoffDates/${signOffDate}`,
      cacheTime: this.shortCacheTime,
      crewMemberId: customCrewMemberId,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting workflow state';
        const lookup: EndPointsErrorLookup = 'PC-019-GET-CHECK-OUT-WORKFLOW-STATE-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateWorkflowState(
    payload: {
      healthCheckStatus?: number;
      hrStatus?: number;
      medicalStatus?: number;
      precheckStatus?: number;
      safetyStatus?: number;
      immigrationStatus?: number;
    },
    shipCode: string,
    signOnDate: string,
    customCrewMemberId?: string,
  ): Observable<IMWStandardResponse> {
    Object.keys(this.cache).forEach(key => {
      if (key.includes('get-/v1/employee/checkin/workflow/ships/')) {
        this.cache[key].invalid = true;
      }
    });
    const options: IHttpClientOptions = {
      method: 'put',
      url: '/v2/employee/checkin/workflow',
      body: {
        crewMemberId: customCrewMemberId ? customCrewMemberId : this.auth.crewMemberId,
        ...payload,
        shipCode,
        signonDate: signOnDate,
        lastUpdateSource: 'PRECHECK',
        lastUpdated: getTimestamp(new Date()),
      },
      cacheTime: 1,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating workflow state';
        const lookup: EndPointsErrorLookup = 'PC-020-PUT-UPDATE-WORKFLOW-STATE-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateDebarkWorkflowState(
    payload: {
      debarkStatus: number;
    },
    shipCode: string,
    signOffDate: string,
    customCrewMemberId?: string,
  ): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'put',
      url: '/v2/employee/checkout/workflow',
      body: {
        crewMemberId: customCrewMemberId ? customCrewMemberId : this.auth.crewMemberId,
        ...payload,
        shipCode,
        signoffDate: signOffDate,
        lastUpdateSource: 'PRECHECK',
        lastUpdated: getTimestamp(new Date()),
      },
      cacheTime: 1,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating checkout workflow state';
        const lookup: EndPointsErrorLookup = 'PC-042-PUT-CHECKOUT-WORKFLOW-STATE-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getCrewPhysique(customCrewMemberId?: string): Observable<IGetCrewCrewPhysiqueResponse> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v1/employee/crewPhysique',
      cacheTime: this.shortCacheTime,
      crewMemberId: customCrewMemberId,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting crew physique';
        const lookup: EndPointsErrorLookup = 'PC-021-GET-CREW-PHYSIQUE-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateCrewPhysique(characteristics: ICharacteristics): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/crewPhysique',
      body: {
        crewMemberId: this.auth.crewMemberId,
        characteristics,
        lastUpdated: getTimestamp(new Date()),
      },
      cacheTime: 1,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating crew physique';
        const lookup: EndPointsErrorLookup = 'PC-022-POST-UPDATE-CREW-PHYSIQUE-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public rateCheckinExperience(shipCode: string, signonDate: string, rating: number): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/checkin/experience/rating',
      body: {
        crewMemberId: this.auth.crewMemberId,
        shipCode,
        signonDate,
        rating,
      },
      cacheTime: 1,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected rating checkin experience';
        const lookup: EndPointsErrorLookup = 'PC-023-POST-RATE-CHECKIN-EXPERIENCE-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getContentFAQS(): Observable<IGetContentFAQS> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v1/employee/content/faqs',
      cacheTime: this.longCacheTime,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting content faqs';
        const lookup: EndPointsErrorLookup = 'PC-024-GET-CONTENT-FAQS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getContentDiseases(): Observable<IGetContentDiseases> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/images/content/excalibur/en/stringStore/PRECHECK_MEDICAL_INFO.mobile.json',
      cacheTime: Infinity,
      useAuthentication: false,
      baseUrl: this.host,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting content diseases';
        const lookup: EndPointsErrorLookup = 'PC-025-GET-CONTENT-DISEASES-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getDocusignRecipientView(params: IDocusignRecipientViewParams): Observable<IGetDocusignResponse> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/docusign/views/recipient',
      body: {
        ...params,
        returnUrl: `${window.document.baseURI}assets/static/docusign.html`,
      },
      cacheTime: 1,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting docusign recipient view';
        const lookup: EndPointsErrorLookup = 'PC-026-POST-RETRIEVING-RECIPIENT-VIEW-URL-FOR-CREWMEMBER-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getDocusignConsoleView(params: IDocusignConsoleViewParams): Observable<IGetDocusignResponse> {
    if (!params.hrManagerCrewMemberId) params.hrManagerCrewMemberId = '000000';
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/docusign/views/console',
      body: params,
      cacheTime: 1,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting docusign console view';
        const lookup: EndPointsErrorLookup = 'PC-043-POST-RETRIEVING-CONSOLE-VIEW-URL-FOR-CREWMEMBER-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getHealthCheckSurveyContent(): Observable<IGetHealthCheckSurveyContent> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: '/v1/employee/healthCheck/survey/content',
      cacheTime: this.longCacheTime,
      useAuthentication: false,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting survey content';
        const lookup: EndPointsErrorLookup = 'PC-028-GET-HEALTH-CHECK-SURVEY-CONTENT-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public deleteMultipleWatermarks(envelopeIds: Array<string>, delayReason?: string): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/docusign/envelopes/recipients/watermarkRecipients',
      body: {
        delayReason,
        envelopeIds,
      },
      cacheTime: 1,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error deleting multiple watermarks';
        const lookup: EndPointsErrorLookup = 'PC-029-POST-DELETE-MULTIPLE-WATERMARKS-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public activateAssignment(
    crewMemberId: string,
    shipCode: string,
    signonDate: string,
    activeStatus = true,
  ): Observable<IMWStandardResponse> {
    const options: IHttpClientOptions = {
      method: 'put',
      url: `/v1/employee/assignments/ships/${shipCode}/signonDates/${signonDate}`,
      body: { activeStatus },
      cacheTime: 1,
      crewMemberId,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error activating assignment';
        const lookup: EndPointsErrorLookup = 'PC-030-PUT-ACTIVE-ASSIGNMENT-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getSOEAFromSharepoint(crewMemberId: string, shipCode: string, signonDate: string): Observable<Blob> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/sharepoint/file?crewMemberId=${crewMemberId}&shipCode=${shipCode}&signonDate=${signonDate}`,
      cacheTime: this.longCacheTime,
      crewMemberId,
      responseType: 'blob',
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting SOEA from sharepoint';
        const lookup: EndPointsErrorLookup = 'PC-031-GET-SOEA-FROM-SHAREPOINT-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public getCrewPhoto(crewId: string): Observable<Blob> {
    const options: IHttpClientOptions = {
      method: 'get',
      url: `/v1/employee/photo/${crewId}`,
      cacheTime: this.longCacheTime,
      responseType: 'blob',
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error getting crew photo';
        const lookup: EndPointsErrorLookup = 'PC-032-GET-CREW-PHOTO-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateCrewPhoto(
    crewId: string,
    shipCode: string,
    lastName: string,
    file: File,
  ): Observable<IMWStandardResponse> {
    const headers = new HttpHeaders()
      .set('Content-Type', file.type)
      .set('Ship-Code', shipCode)
      .set('Last-Name', lastName.toUpperCase());

    const options: IHttpClientOptions = {
      method: 'put',
      url: `/v1/employee/photo/${crewId}`,
      body: file,
      crewMemberId: crewId,
      additionalHeaders: headers,
      timeOut: 120000,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating crew photo';
        const lookup: EndPointsErrorLookup = 'PC-033-PUT-UPDATE-CREW-PHOTO-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public updateCrewProfile(crewMemberId: string, profile: Partial<Profile>): Observable<any> {
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/v1/employee/profiles/phones',
      body: { crewMemberId, ...profile.data },
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error updating crew profile';
        const lookup: EndPointsErrorLookup = 'PC-044-POST-UPDATE-CREW-PROFILE-INFO';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public syncCrewPhoto(crewId: string, shipCode: string, lastName: string): Observable<IMWStandardResponse> {
    const headers = new HttpHeaders().set('Ship-Code', shipCode).set('Last-Name', lastName.toUpperCase());

    const options: IHttpClientOptions = {
      method: 'put',
      url: `/v1/employee/photo/sync/${crewId}`,
      crewMemberId: crewId,
      additionalHeaders: headers,
    };

    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error syncing crew photo';
        const lookup: EndPointsErrorLookup = 'PC-034-PUT-SYNC-CREW-PHOTO-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public addVaccineCard(vaccineCardBody: any): Observable<IMWStandardResponse> {
    const body: IVaccineCard = {
      ...vaccineCardBody,
      crewId: this.auth.crewMemberId,
    };
    const options: IHttpClientOptions = {
      method: 'post',
      url: '/seacare/crew-members/document-submissions',
      body,
      baseUrl: this.host,
    };
    return this.defaultHttpClient(options).pipe(
      catchError(err => {
        const message = 'unexpected error adding vaccine card';
        const lookup: EndPointsErrorLookup = 'PC-036-POST-ADD-VACCINE-CARD-FAILED';
        return this.defautlTelemetryHttpCatch(err, { message, lookup });
      }),
    );
  }

  public extractErrorMessage(err: any): string {
    const [error] = err?.error?.errors ?? [];
    if (error) {
      const { userMessage, developerMessage, internalMessage } = error;
      return userMessage ?? developerMessage ?? internalMessage;
    }

    const { name, status, message } = err ?? {};

    if (name === 'TimeoutError') {
      return 'There was a problem getting your online information, please try again later';
    }

    if (typeof status === 'number') {
      if (status === 0) return 'Sorry, we can’t get the information right now, so please try again later';
      if (status === 401) return 'Your session has expired';
      return 'Sorry, we are not able to process your request, please check your information, and try again';
    }

    if (message) return message;

    return 'There was an unknown error';
  }

  private addCachedPayload(key: string, payload: any, invalid = false): void {
    this.cache[key] = {
      date: Date.now(),
      payload,
      invalid,
    };
  }

  private defaultHttpCatch(err: HttpErrorResponse | any): Observable<string> {
    console.warn('[MW] Error', err);
    const errorMsg = this.extractErrorMessage(err);
    if (err && err.status && err.status === 401) {
      setTimeout(() => {
        this.spinner.show();
      }, 500);
      setTimeout(() => this.auth.redirectToLogin(), 5000);
    }
    return throwError(errorMsg);
  }

  private defautlTelemetryHttpCatch(
    err: HttpErrorResponse | any,
    { lookup, message }: { lookup: EndPointsErrorLookup; message: string },
  ): Observable<any> {
    const context = this.splunkService.initializeContext(
      ['error', 'message'],
      [this.splunkService.getTelemetryErrorObject(err), message],
    );
    this.splunkService.errorByLookUp(lookup, { context });
    return this.defaultHttpCatch(err);
  }

  private defaultHttpClient(options: IHttpClientOptions): Observable<any> {
    const {
      method = 'get',
      url,
      body,
      crewMemberId,
      responseType = 'json',
      useAuthentication = true,
      baseUrl = this.baseUrl,
      additionalHeaders,
      timeOut = 11000,
    } = options;

    console.log(`[MW] ${method} ${url}`);

    const finalUrl = baseUrl + url;

    const httpOptions = this.defaultOptions;
    if (crewMemberId) httpOptions.headers = httpOptions.headers.set('Crew-Member-Id', crewMemberId);
    if (responseType) httpOptions.responseType = responseType;
    if (!useAuthentication) httpOptions.headers = httpOptions.headers.delete('sessionId');
    if (additionalHeaders) {
      additionalHeaders.keys().forEach(header => {
        httpOptions.headers = httpOptions.headers.set(header, additionalHeaders.get(header));
      });
    }
    const cacheTime = options.cacheTime || this.shortCacheTime;
    const cacheKey = `${crewMemberId || this.auth.crewMemberId}-${method}-${url}`;
    const cacheValue = this.cache[cacheKey];
    const cacheExpiredDate = Date.now() - cacheTime;

    let req$: Observable<any>;

    if (cacheValue && !cacheValue.invalid && cacheValue.date > cacheExpiredDate) {
      console.log(`[MW] (cache) ${method} ${url}`);
      return of(cacheValue.payload);
    } else {
      switch (method) {
        case 'get':
          req$ = this.http.get(finalUrl, httpOptions);
          break;
        case 'post':
          req$ = this.http.post(finalUrl, body, httpOptions);
          break;
        case 'put':
          req$ = this.http.put(finalUrl, body, httpOptions);
          break;
        case 'delete':
          req$ = this.http.delete(finalUrl, httpOptions);
          break;
        case 'mock':
          req$ = of(body);
          break;
        default:
          throw new Error('Missing method');
      }
    }
    return req$.pipe(
      timeout(timeOut),
      retryWhen(errors =>
        errors.pipe(
          delay(100),
          take(1),
          // tslint:disable-next-line:no-unnecessary-callback-wrapper
          concatMap(err => throwError(err)),
        ),
      ),
      tap(res => this.addCachedPayload(cacheKey, res)),
      tap(_ => console.log(`[MW] (ok) ${method} ${url}`)),
      catchError(err => {
        console.log(`[MW] (error) ${method} ${url}`);
        this.addCachedPayload(cacheKey, { status: err.status || err }, true);
        return throwError(err);
      }),
    );
  }
}
